import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "@/hooks/use-translation";
import { FormValues, Item, MainProduct, Option } from "@/types/productTypes";

export interface SummaryStepProps {
   product: MainProduct;
}

export type SummaryRow =
   | { type: "single"; option: Option; item: Item }
   | { type: "multiple"; option: Option; items: Item[] };

export const Summary = ({ product }: SummaryStepProps) => {
   const { watch } = useFormContext<FormValues>();
   const allValues = watch();
   const [summaryRows, setSummaryRows] = useState<SummaryRow[]>([]);
   const { translate, translation } = useTranslation();
   useEffect(() => {
      const rows: SummaryRow[] = [];

      product.stages.forEach((stage) => {
         stage.options?.forEach((opt) => {
            if (!opt.id) return;
            const val = allValues[opt.id.toString()];
            if (!val || !("selectedItem" in val)) return;

            const sel = val.selectedItem;
            if (!sel) return;

            if (Array.isArray(sel)) {
               const items = sel
                  .map((id) => opt.items?.find((i) => i.id?.toString() === id))
                  .filter(Boolean) as Item[];
               if (items.length)
                  rows.push({ type: "multiple", option: opt, items });
            } else {
               const it = opt.items?.find((i) => i.id?.toString() === sel);
               if (it) rows.push({ type: "single", option: opt, item: it });
            }
         });
      });

      setSummaryRows(rows);
   }, [allValues, product]);

   return (
      <div className="mb-8">
         {/* Heading */}
         <h3 className="text-d-h2 text-custom-dark">
            {translation.configurator.stages.Summary}
         </h3>

         {/* Rows */}
         <div className="mt-4 flex flex-col gap-4">
            {summaryRows.map((row, idx) => (
               <div key={idx} className="flex flex-col gap-4">
                  {/* Option label */}
                  <div className="text-[14px] leading-[172%] uppercase text-custom-dark">
                     {translate(row.option)}
                  </div>

                  <div className="flex items-start justify-between gap-8">
                     <span className="flex-1 text-[14px] leading-[172%] text-custom-dark/60">
                        {row.type === "single"
                           ? translate(row.item)
                           : row.items.map((i) => translate(i)).join(", ")}
                     </span>
                  </div>

                  {/* divider */}
                  <div className="border-t border-custom-dark/10" />
               </div>
            ))}
         </div>
      </div>
   );
};
